import { useState } from "react"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog"
import { MoreHorizontalIcon, PencilIcon, TrashIcon } from "lucide-react"
import { useToast } from "@/hooks/use-toast"
import { usePromotions, useDeletePromotion, formatPromotionPrice, type Promotion } from "@/api/promotions"
import { PromotionDialog } from "./promotion-dialog"
import { TableEmptyRow, TableLoadingRows } from "@/components/shared/table-states"

interface PromotionsTableProps {
  searchQuery: string
}

function getPromotionStatus(promotion: Promotion): "active" | "inactive" | "expired" | "scheduled" {
  const now = new Date()
  const start = new Date(promotion.startDate)
  const end = new Date(promotion.endDate)

  if (!promotion.isActive) return "inactive"
  if (now > end) return "expired"
  if (now < start) return "scheduled"
  return "active"
}

export function PromotionsTable({ searchQuery }: PromotionsTableProps) {
  const { toast } = useToast()
  const { data: promotions = [], isLoading } = usePromotions()
  const deletePromotion = useDeletePromotion()

  const [editingPromotion, setEditingPromotion] = useState<Promotion | null>(null)
  const [deletingId, setDeletingId] = useState<string | null>(null)

  const filtered = promotions.filter(
    (p) =>
      p.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      p.description.toLowerCase().includes(searchQuery.toLowerCase())
  )

  const handleDelete = async () => {
    if (!deletingId) return
    try {
      await deletePromotion.mutateAsync(deletingId)
      toast({ title: "Promotion deleted" })
    } catch {
      toast({ title: "Error", description: "Could not delete promotion", variant: "destructive" })
    } finally {
      setDeletingId(null)
    }
  }

  const statusBadge = (promotion: Promotion) => {
    const status = getPromotionStatus(promotion)
    const variants: Record<typeof status, "default" | "secondary" | "outline" | "destructive"> = {
      active: "default",
      inactive: "secondary",
      expired: "outline",
      scheduled: "secondary",
    }
    const labels: Record<typeof status, string> = {
      active: "Active",
      inactive: "Inactive",
      expired: "Expired",
      scheduled: "Scheduled",
    }
    return <Badge variant={variants[status]}>{labels[status]}</Badge>
  }

  return (
    <>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="w-16">Photo</TableHead>
            <TableHead>Title</TableHead>
            <TableHead>Price</TableHead>
            <TableHead>Start</TableHead>
            <TableHead>End</TableHead>
            <TableHead>Status</TableHead>
            <TableHead className="text-right">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {isLoading ? (
            <TableLoadingRows colSpan={7} />
          ) : !filtered.length ? (
            <TableEmptyRow
              colSpan={7}
              message={searchQuery ? "No promotions match your search." : "No promotions yet."}
            />
          ) : (
            filtered.map((promotion) => (
            <TableRow key={promotion._id}>
              <TableCell>
                <div className="h-10 w-16 overflow-hidden rounded">
                  <img
                    src={promotion.photo}
                    alt={promotion.title}
                    className="h-full w-full object-cover"
                  />
                </div>
              </TableCell>
              <TableCell>
                <div className="font-medium">{promotion.title}</div>
                <div className="text-xs text-muted-foreground line-clamp-1">{promotion.description}</div>
              </TableCell>
              <TableCell>
                <div className="flex flex-col">
                  {promotion.discountedPrice != null ? (
                    <>
                      <span className="font-medium">
                        {formatPromotionPrice(promotion.discountedPrice, promotion.currency)}
                      </span>
                      <span className="text-xs text-muted-foreground line-through">
                        {formatPromotionPrice(promotion.price, promotion.currency)}
                      </span>
                    </>
                  ) : (
                    <span className="font-medium">
                      {formatPromotionPrice(promotion.price, promotion.currency)}
                    </span>
                  )}
                  {promotion.currency && promotion.currency !== 'ALL' && (
                    <span className="text-xs text-muted-foreground">{promotion.currency}</span>
                  )}
                </div>
              </TableCell>
              <TableCell className="text-sm">
                {new Date(promotion.startDate).toLocaleDateString()}
              </TableCell>
              <TableCell className="text-sm">
                {new Date(promotion.endDate).toLocaleDateString()}
              </TableCell>
              <TableCell>{statusBadge(promotion)}</TableCell>
              <TableCell className="text-right">
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="icon">
                      <MoreHorizontalIcon className="h-4 w-4" />
                      <span className="sr-only">Actions</span>
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem onClick={() => setEditingPromotion(promotion)}>
                      <PencilIcon className="mr-2 h-4 w-4" />
                      Edit
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      className="text-destructive"
                      onClick={() => setDeletingId(promotion._id)}
                    >
                      <TrashIcon className="mr-2 h-4 w-4" />
                      Delete
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </TableCell>
            </TableRow>
            ))
          )}
        </TableBody>
      </Table>

      <PromotionDialog
        open={!!editingPromotion}
        onOpenChange={(open) => { if (!open) setEditingPromotion(null) }}
        promotion={editingPromotion}
      />

      <AlertDialog open={!!deletingId} onOpenChange={(open) => { if (!open) setDeletingId(null) }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete promotion?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete the promotion and its photo. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete}>Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  )
}
